
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import AlertSystem from '../sections/AlertSystem';
import type { Permission } from '../../types/permissions';

interface NavbarProps {
  activeSection: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  permission: Permission;
}

export default function Navbar({ activeSection }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAlertPanelOpen, setIsAlertPanelOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const { user, logout } = useAuth();
  const { hasPermission, roleName } = usePermissions();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar menú móvil con Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setShowUserMenu(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const menuItems: MenuItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', path: '/dashboard', permission: 'view:dashboard' },
    { id: 'registro', label: 'Registro', icon: 'ri-clipboard-line', path: '/registro', permission: 'view:registro' },
    { id: 'scuba3s', label: 'Scuba3s', icon: 'ri-radar-line', path: '/scuba3s', permission: 'view:scuba3s' },
    { id: 'historial-alertas', label: 'Historial', icon: 'ri-history-line', path: '/historial-alertas', permission: 'view:historial-alertas' },
    { id: 'analisis', label: 'Análisis', icon: 'ri-line-chart-line', path: '/analisis', permission: 'view:analisis' },
    { id: 'reportes', label: 'Reportes', icon: 'ri-file-chart-line', path: '/reportes', permission: 'view:reportes' },
    { id: 'quimicos', label: 'Químicos', icon: 'ri-flask-line', path: '/quimicos', permission: 'view:quimicos' },
    { id: 'inventario', label: 'Inventario', icon: 'ri-archive-line', path: '/inventario', permission: 'view:inventario' },
    { id: 'configuracion', label: 'Configuración', icon: 'ri-settings-3-line', path: '/configuracion', permission: 'view:configuracion' },
    { id: 'admin', label: 'Admin', icon: 'ri-shield-star-line', path: '/admin', permission: 'view:admin' }
  ];

  // Filter menu items based on user permissions
  const visibleMenuItems = menuItems.filter(item => hasPermission(item.permission));


  const handleNavigation = (item: MenuItem) => {
    navigate(item.path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white/90 backdrop-blur-sm'
      }`}>
        <div className="w-full mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 gap-4">
            {/* Logo - Más compacto */}
            <div
              className="flex items-center space-x-2 cursor-pointer flex-shrink-0"
              onClick={() => navigate('/dashboard')}
            >
              <img
                src="https://public.readdy.ai/ai/img_res/a767d65d-0e19-49c9-9380-a6816d196f72.png"
                alt="PoolChem Logo"
                className="w-8 h-8 object-contain"
              />
              <span className="text-lg font-bold text-teal-600 hidden sm:block">PoolChem</span>
              <span className="text-lg font-bold text-teal-600 block sm:hidden">PC</span>
            </div>

            {/* Navigation Menu - Solo pantallas grandes */}
            <div className="hidden lg:flex items-center flex-1 justify-center overflow-visible">
              <div className="flex items-center space-x-2 lg:space-x-3">
                {visibleMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item)}
                    className={`flex items-center space-x-2 px-3 py-2 lg:px-4 lg:py-2 rounded-lg transition-all duration-200 cursor-pointer text-sm min-w-fit ${
                      activeSection === item.id
                        ? 'bg-teal-50 text-teal-600 border-b-2 border-teal-500'
                        : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                    }`}
                  >
                    <i className={`${item.icon} text-lg flex-shrink-0`}></i>
                    <span className="font-medium whitespace-nowrap hidden lg:block">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Section - Más compacto */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* Tiempo - Solo pantallas grandes */}
              <div className="hidden xl:flex items-center space-x-2 text-xs text-gray-500">
                <i className="ri-time-line"></i>
                <span>08:30</span>
              </div>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setIsAlertPanelOpen(true)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ${
                    alertCount > 0
                      ? 'bg-red-100 hover:bg-red-200 animate-pulse'
                      : 'bg-teal-100 hover:bg-teal-200'
                  }`}
                >
                  <i className={`ri-notification-3-line text-sm ${alertCount > 0 ? 'text-red-600' : 'text-teal-600'}`}></i>
                </button>
                {alertCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce">
                    {alertCount > 9 ? '9+' : alertCount}
                  </span>
                )}
              </div>

              {/* User Menu - Más compacto */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-1 p-1 rounded-lg hover:bg-teal-50 transition-colors"
                >
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-teal-600 text-sm"></i>
                  </div>
                  <div className="hidden sm:block text-left max-w-32">
                    <div className="text-xs font-medium text-gray-900 truncate">{user?.email}</div>
                    <div className="text-xs text-gray-500 truncate">{roleName}</div>
                  </div>
                  <i className="ri-arrow-down-s-line text-gray-400 text-sm"></i>
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">{user?.email}</div>
                      <div className="text-xs text-gray-500">
                        Tenant: {user?.tenantId.slice(0, 8)}...
                      </div>
                    </div>
                    {hasPermission('view:configuracion') && (
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          navigate('/configuracion');
                        }}
                        className="w-full flex items-center space-x-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-50"
                      >
                        <i className="ri-settings-3-line"></i>
                        <span>Configuración</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        handleLogout();
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-left text-red-600 hover:bg-red-50"
                    >
                      <i className="ri-logout-circle-line"></i>
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="w-8 h-8 flex items-center justify-center text-gray-600 cursor-pointer hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg`}></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Mejorado */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t shadow-lg">
            <div className="px-6 py-4 space-y-2 max-h-96 overflow-y-auto">
              {/* User info en móvil */}
              <div className="flex items-center space-x-3 px-3 py-2 bg-gray-50 rounded-lg mb-3">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-teal-600 text-sm"></i>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">{user?.email}</div>
                  <div className="text-xs text-gray-500">{roleName}</div>
                </div>
              </div>

              {/* Navigation items */}
              <div className="grid grid-cols-2 gap-3">
                {visibleMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      handleNavigation(item);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex flex-col items-center space-y-2 px-4 py-4 rounded-lg transition-all cursor-pointer ${
                      activeSection === item.id
                        ? 'bg-teal-50 text-teal-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${item.icon} text-xl`}></i>
                    <span className="text-xs font-medium text-center">{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Acciones rápidas en móvil */}
              <div className="pt-3 mt-3 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <button
                    onClick={() => {
                      setIsAlertPanelOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                      alertCount > 0
                        ? 'bg-red-50 text-red-600'
                        : 'bg-teal-50 text-teal-600'
                    }`}
                  >
                    <i className="ri-notification-3-line"></i>
                    <span className="text-sm">Alertas</span>
                    {alertCount > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        {alertCount > 9 ? '9+' : alertCount}
                      </span>
                    )}
                  </button>

                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <i className="ri-time-line"></i>
                    <span>Última: 08:30</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 mt-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                >
                  <i className="ri-logout-circle-line"></i>
                  <span className="text-sm font-medium">Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Sistema de Alertas */}
      <AlertSystem
        isOpen={isAlertPanelOpen}
        onClose={() => setIsAlertPanelOpen(false)}
        onAlertCountChange={setAlertCount}
      />
    </>
  );
}
